import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiArrowRight, FiBriefcase, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { fmtDate } from '../../utils/istDate';

const STATUS_META = {
    ACTIVE: { label: 'Active', color: '#16a34a', bg: '#dcfce7' },
    INACTIVE: { label: 'Inactive', color: '#64748b', bg: '#f1f5f9' },
    PLANNING: { label: 'Planning', color: '#d97706', bg: '#fef3c7' },
    IN_PROGRESS: { label: 'In Progress', color: '#2563eb', bg: '#dbeafe' },
    REVIEW: { label: 'Review', color: '#7c3aed', bg: '#ede9fe' },
    COMPLETED: { label: 'Completed', color: '#059669', bg: '#d1fae5' },
    ON_HOLD: { label: 'On Hold', color: '#ea580c', bg: '#ffedd5' },
    CANCELLED: { label: 'Cancelled', color: '#dc2626', bg: '#fee2e2' },
    ARCHIVED: { label: 'Archived', color: '#64748b', bg: '#f1f5f9' }
};

export default function ProjectsList({ projects = [], loading = false, isAdmin = false, onEdit, onDelete }) {
  const navigate = useNavigate();

  const fmt = (d) => fmtDate(d) || null;

  if (loading) {
    return <div className="py-12 text-center text-slate-500 font-medium w-full">Loading projects...</div>;
  }

  if (projects.length === 0) {
    return (
      <div className="w-full py-16 text-center bg-white rounded-2xl border border-slate-200 border-dashed">
        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <FiBriefcase className="text-2xl text-slate-400" />
        </div>
        <h3 className="text-lg font-bold text-slate-800 mb-1">No projects found</h3>
        <p className="text-slate-500 text-sm mb-4 max-w-sm mx-auto">There are no projects available to display here.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5 w-full">
      {projects.map(project => {
        const statusMeta = STATUS_META[project.status] || STATUS_META.PLANNING;
        
        return (
          <div 
            key={project.id || project.projectId} 
            className="bg-white rounded-xl border border-slate-200 shadow-2xs hover:shadow-xs hover:border-blue-300 transition-all overflow-hidden flex flex-col group cursor-pointer" 
            onClick={() => navigate(`/ProjectDetails/${project.id || project.projectId}`, { state: { productId: project.productId || project.product?.id } })}
          >
            <div className="p-3.5 sm:p-4 flex-1 flex flex-col">
              <div className="flex justify-between items-center mb-2">
                <span className="px-2 py-0.5 bg-blue-50/80 rounded-md text-[11px] font-bold tracking-wider uppercase border border-blue-200/60 text-blue-700">
                  {project.projectCode || 'PRJ'}
                </span>
                
                {project.endDate && new Date(project.endDate) < new Date() && project.status !== 'COMPLETED' ? (
                  <span className="px-2 py-0.5 bg-rose-50 text-rose-700 rounded-md text-[10px] font-bold uppercase tracking-wider border border-rose-200">Overdue</span>
                ) : project.status ? (
                  <span 
                    className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border"
                    style={{ backgroundColor: statusMeta.bg, color: statusMeta.color, borderColor: `${statusMeta.color}30` }}
                  >
                    {statusMeta.label}
                  </span>
                ) : null}
              </div>
              
              {project.product && (
                <div className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider mb-0.5">
                  {project.product.productname || project.product.productName || project.product.name}
                </div>
              )}
              <h3 className="text-sm font-bold text-slate-800 mb-1 group-hover:text-blue-600 transition-colors line-clamp-1">{project.projectName}</h3>
              <p className="text-xs text-slate-500 line-clamp-1 mb-2.5">
                {project.description || <span className="text-slate-400 italic">No description provided.</span>}
              </p>

              <div className="flex flex-wrap items-center gap-3 text-[11px] font-medium text-slate-500 mt-auto pt-1">
                <div className="flex items-center gap-1 text-slate-600">
                  <FiCalendar className="text-slate-400 text-xs" />
                  <span>Due: {fmt(project.endDate) || '—'}</span>
                </div>
                {project.projectManagerId && (
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-600">
                      {project.projectManagerId.substring(0,2).toUpperCase()}
                    </div>
                    <span className="truncate max-w-[90px]">{project.projectManagerId}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="px-3.5 sm:px-4 py-2 bg-slate-50/70 border-t border-slate-100 flex items-center justify-between mt-auto group-hover:bg-blue-50/40 transition-colors">
              <span className="text-xs font-semibold text-blue-600 group-hover:text-blue-700 flex items-center gap-1">
                Open Project <FiArrowRight size={11} className="transform group-hover:translate-x-0.5 transition-transform" />
              </span>
              {isAdmin && (
                <div className="flex items-center gap-1">
                  <button 
                    onClick={(e) => { e.stopPropagation(); onEdit && onEdit(project); }}
                    className="p-1 text-slate-400 hover:text-blue-600 hover:bg-white rounded transition-colors"
                    title="Edit Project"
                  >
                    <FiEdit2 size={12} />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDelete && onDelete(project.id || project.projectId); }}
                    className="p-1 text-slate-400 hover:text-red-500 hover:bg-white rounded transition-colors"
                    title="Delete Project"
                  >
                    <FiTrash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
